import { Receipt, CreditCard, FileText, StickyNote } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    invoiceId: string;
    invoiceDate: string;
    dueDate?: string;
    paymentStatus: string;
    paymentMode?: string;
    transactionId?: string;
    billedTo: { name: string; address?: string; nhcId: string };
    items: {
      description: string;
      sacCode?: string;
      rate: number;
      qty: number;
      gst: number;
      amount: number;
    }[];
    subtotal: number;
    gstTotal: number;
    discount?: number;
    insuranceCovered?: number;
    grandTotal: number;
    amountPaid: number;
    balanceDue: number;
    notes?: string[];
    signatories: {
      name: string;
      role: string;
      nhcId?: string;
      licenseNo?: string;
      institution?: string;
    }[];
  };
}

function formatCurrency(amount: number): string {
  return `Rs. ${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getPaymentStatusStyle(status: string) {
  const s = (status || '').toUpperCase();
  if (s === 'PAID' || s === 'SETTLED') return 'bg-green-100 text-green-700 border-green-300';
  if (s === 'PARTIAL' || s === 'PARTIALLY PAID') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
  if (s === 'UNPAID' || s === 'OVERDUE') return 'bg-red-100 text-red-700 border-red-300';
  return 'bg-gray-100 text-gray-700 border-gray-300';
}

export default function InvoiceReceiptTemplate({ data }: Props) {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-gray-700" />
          <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
            Invoice / Receipt
          </span>
        </div>
        <span
          className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded border ${getPaymentStatusStyle(data.paymentStatus)}`}
        >
          {data.paymentStatus}
        </span>
      </div>

      {/* Invoice Meta & Billed To */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 bg-gray-50 px-3 py-2.5 border border-gray-200 rounded text-[10px]">
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Invoice ID:</span>
          <span className="text-gray-800 font-mono">{data.invoiceId}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Invoice Date:</span>
          <span className="text-gray-800">{data.invoiceDate}</span>
        </div>
        {data.dueDate && (
          <div className="flex gap-2">
            <span className="font-semibold text-gray-600">Due Date:</span>
            <span className="text-gray-800">{data.dueDate}</span>
          </div>
        )}
      </div>

      {/* Billed To */}
      <div className="bg-gray-50 px-3 py-2.5 border border-gray-200 rounded text-[10px]">
        <h3 className="text-[9px] font-bold uppercase tracking-wider text-gray-600 mb-1">
          Billed To
        </h3>
        <p className="text-gray-800 font-medium">{data.billedTo.name}</p>
        <p className="text-gray-500 font-mono text-[9px]">NHC ID: {data.billedTo.nhcId}</p>
        {data.billedTo.address && (
          <p className="text-gray-600 mt-0.5">{data.billedTo.address}</p>
        )}
      </div>

      {/* Items Table */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <FileText className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Line Items
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[10px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Description
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  SAC Code
                </th>
                <th className="text-right font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Rate
                </th>
                <th className="text-center font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Qty
                </th>
                <th className="text-right font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  GST%
                </th>
                <th className="text-right font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800 font-medium">
                    {item.description}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-600 font-mono">
                    {item.sacCode || '—'}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800 text-right font-mono">
                    {formatCurrency(item.rate)}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800 text-center">
                    {item.qty}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800 text-right">
                    {item.gst}%
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800 text-right font-mono font-medium">
                    {formatCurrency(item.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals Section */}
      <div className="flex justify-end">
        <div className="w-64 bg-gray-50 border border-gray-200 rounded text-[10px]">
          <div className="flex justify-between py-1 px-3 border-b border-gray-200">
            <span className="text-gray-600">Subtotal</span>
            <span className="text-gray-800 font-mono">{formatCurrency(data.subtotal)}</span>
          </div>
          <div className="flex justify-between py-1 px-3 border-b border-gray-200">
            <span className="text-gray-600">GST</span>
            <span className="text-gray-800 font-mono">{formatCurrency(data.gstTotal)}</span>
          </div>
          {data.discount !== undefined && data.discount > 0 && (
            <div className="flex justify-between py-1 px-3 border-b border-gray-200">
              <span className="text-gray-600">Discount</span>
              <span className="text-green-700 font-mono">- {formatCurrency(data.discount)}</span>
            </div>
          )}
          {data.insuranceCovered !== undefined && data.insuranceCovered > 0 && (
            <div className="flex justify-between py-1 px-3 border-b border-gray-200">
              <span className="text-gray-600">Insurance Covered</span>
              <span className="text-blue-700 font-mono">- {formatCurrency(data.insuranceCovered)}</span>
            </div>
          )}
          <div className="flex justify-between py-1.5 px-3 border-b border-gray-300 bg-gray-100 font-bold">
            <span className="text-gray-800">Grand Total</span>
            <span className="text-gray-900 font-mono">{formatCurrency(data.grandTotal)}</span>
          </div>
          <div className="flex justify-between py-1 px-3 border-b border-gray-200">
            <span className="text-gray-600">Amount Paid</span>
            <span className="text-green-700 font-mono">{formatCurrency(data.amountPaid)}</span>
          </div>
          <div className="flex justify-between py-1.5 px-3 font-bold">
            <span className="text-gray-800">Balance Due</span>
            <span className={`font-mono ${data.balanceDue > 0 ? 'text-red-700' : 'text-green-700'}`}>
              {formatCurrency(data.balanceDue)}
            </span>
          </div>
        </div>
      </div>

      {/* Payment Info */}
      {(data.paymentMode || data.transactionId) && (
        <div className="bg-gray-50 px-3 py-2.5 border border-gray-200 rounded text-[10px]">
          <div className="flex items-center gap-1.5 mb-1">
            <CreditCard className="w-3 h-3 text-gray-600" />
            <h3 className="text-[9px] font-bold uppercase tracking-wider text-gray-600">
              Payment Information
            </h3>
          </div>
          <div className="flex gap-3">
            {data.paymentMode && (
              <div className="flex gap-2">
                <span className="font-semibold text-gray-600">Mode:</span>
                <span className="text-gray-800">{data.paymentMode}</span>
              </div>
            )}
            {data.transactionId && (
              <div className="flex gap-2">
                <span className="font-semibold text-gray-600">Transaction ID:</span>
                <span className="text-gray-800 font-mono">{data.transactionId}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes */}
      {data.notes && data.notes.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <StickyNote className="w-3 h-3 text-gray-600" />
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
              Notes
            </h3>
          </div>
          <ul className="list-disc list-inside space-y-0.5 text-[10px] text-gray-700 bg-gray-50 px-3 py-2 border border-gray-200 rounded">
            {data.notes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Digital Signatures */}
      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
